<template>
  <LayoutOverlay class="modal-edit-link" @closeOverlay="closeModal()">
    <div class="container">
      <h2>
        {{
          editMode
            ? t('general__text__change') + ' ' + t('general__data__link').toLowerCase()
            : t('general__data__link-add-new')
        }}
      </h2>
      <div class="content">
        <TextFieldStandard
          :error="!linkName"
          :error-message="'* ' + t('general__text__required-field')"
          :hideIconX="true"
          :label="t('general__text__name') + ' *'"
          :placeHolderText="
            namePrefilledText.length ? namePrefilledText : t('general__data__link-add-name')
          "
          :prefilledText="namePrefilledText"
          @emitInput="updateName"
        />
        <TextFieldStandard
          :error="!linkUrl"
          :error-message="'* ' + t('general__text__required-field')"
          :hideIconX="true"
          label="URL *"
          placeHolderText="https://www.example.com"
          :prefilledText="urlPrefilledText"
          @emitInput="updateUrl"
        />
      </div>
      <div class="footer">
        <ButtonOutline
          class="button"
          :buttonText="t('general__text__cancel')"
          size="small"
          @onClicked="closeModal()"
        ></ButtonOutline>
        <ButtonPrimary
          class="button"
          :buttonText="editMode ? t('general__data__save-changes') : t('general__data__link-add')"
          :disabled="
            !linkName.length ||
            !linkUrl.length ||
            ((editMode && linkName === namePrefilledText && linkUrl === urlPrefilledText) ?? false)
          "
          size="small"
          @onClicked="saveLink()"
        ></ButtonPrimary>
      </div>
    </div>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import {
  CreateLinkAttachment,
  postLinkAttachment,
  updateAttachment,
  UpdateLinkAttachment
} from '@/services/attachments.service'
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'
// emits
interface EMITS {
  (e: 'closeEditLinkModal', val: boolean)
}
const emits = defineEmits<EMITS>()

// variables
const store = useStore()
const piniaStore = useGeneralStore()
const { t } = useI18n()
const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)

const currentOverlayModalOnModalPinia = computed(() => {
  return piniaStore.currentOverlayModalOnModalPinia
})
const editMode = computed(() => piniaStore.selectedLink)
const editedLink = ref<CreateLinkAttachment | UpdateLinkAttachment>({
  entityId: editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.entityId : '',
  name: editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.name : '',
  url: editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.url : '',
  entityType: editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.entityType : ''
})
const isTicketLink = computed(() => {
  return (
    showOverlayModalOnModalPinia.value && currentOverlayModalOnModalPinia.value === 'editTicketLink'
  )
})
const linkName = ref<string>(
  editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.name : ''
)
const linkUrl = ref<string>(
  editMode.value && piniaStore.selectedLink ? piniaStore.selectedLink.url : ''
)
const namePrefilledText = computed(() => {
  if (editMode.value && piniaStore.selectedLink) return piniaStore.selectedLink.name
  else return ''
})
const selectedObjectId = computed(() => {
  return store.getters.selectedObjectId
})
const selectedTicketId = computed(() => {
  return piniaStore.selectedTicket?.id
})
const showOverlayModalOnModalPinia = computed(() => {
  return piniaStore.showOverlayModalOnModalPinia
})
const urlPrefilledText = computed(() => {
  if (editMode.value && piniaStore.selectedLink) return piniaStore.selectedLink.url
  else return ''
})
// methods
function closeModal() {
  piniaStore.setSelectedLink(null)
  emits('closeEditLinkModal', false)
}
async function saveLink() {
  editedLink.value.name = linkName.value
  editedLink.value.url = linkUrl.value
  if (editedLink.value.name === '') {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__missing-name')
    })
    return
  }
  if (editedLink.value.url === '') {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__missing-url')
    })
    return
  }
  try {
    let result
    let selectedEntityType
    if (isTicketLink.value) selectedEntityType = 'TICKET'
    else
      selectedEntityType = clickedEntityType.value === 'REGION' ? 'REALM' : clickedEntityType.value

    if (editMode.value && piniaStore.selectedLink)
      result = await updateAttachment(piniaStore.selectedLink.attachmentId, {
        name: editedLink.value.name,
        url: editedLink.value.url,
        entityType: editedLink.value.entityType ?? selectedEntityType
      })
    else
      result = await postLinkAttachment({
        entityId: isTicketLink.value ? selectedTicketId.value : selectedObjectId.value,
        name: editedLink.value.name,
        url: editedLink.value.url,
        entityType: selectedEntityType
      })
    if (result?.data) {
      store.commit('setInfoMessage', {
        type: 'success',
        message: t('general__data__link-saved')
      })
      piniaStore.setLinksUpdated(result.data)
    } else {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__text__something-went-wrong-could-not-save-link')}`
      })
    }
  } catch (error) {
    console.error(error)
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__text__something-went-wrong-could-not-save-link')}`
    })
  }
  closeModal()
}

function updateName(val: string | number) {
  if (typeof val === 'number') val = val.toString()
  linkName.value = val
}
function updateUrl(val: string | number) {
  if (typeof val === 'number') val = val.toString()
  linkUrl.value = val
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.container {
  background-color: variables.$color-background;
  border-radius: variables.$border-radius-small;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  left: 50%;
  max-height: 650px;
  padding: 24px;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
  width: 700px;
  .content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    justify-content: space-evenly;
    padding: 32px 0;
    .button {
      width: max-content;
    }
  }
  .footer {
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-large;
    justify-content: flex-end;
  }
  h2 {
    @include font-mixins.desktop-bold-h4;
    color: variables.$color-greyscale-content;
    margin-top: 2px;
  }
}
@media screen and (max-width: 768px) {
  .container {
    display: block;
    max-height: 650px;
    @include mixins.scrollbar-vertical;
    overflow-y: auto;
    top: 50%;
    width: 300px;
    .content {
      max-width: 268px;
    }
  }
}
@media screen and (max-height: 670px) {
  .container {
    max-height: 500px;
  }
}
</style>
